import type { z } from "zod/v4";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import {
  buildFieldLabelMap,
  resolveDefaultTableColumns,
  resolveDefaultVisibleColumns,
} from "@/lib/ai/visualization/views/shared/presentation-defaults";
import {
  buildSpecValidationIssue,
  pushDuplicateValueIssues,
  pushMappedKeyIssues,
  pushSelectedKeyIssues,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";
import type { tableAuthoringSchema } from "@/lib/ai/visualization/views/table/schema";

type TableSpec = Extract<VizSpec, { type: "table" }>;
type TableAuthoring = z.infer<typeof tableAuthoringSchema>;

/**
 * table surface が selected columns / row range と矛盾しないかを検証する。
 *
 * @remarks
 * pinning や sizing は dataset row/column と密接に結びつくため、
 * view compile 後にまとめて整合確認するほうが drift しにくい。
 */
export function validateTableSpec(
  args: ValidateVizSpecArgs<TableSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];

  if (args.spec.columns.length === 0) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.columns",
        code: "missing-columns",
        message: "must include at least one visible column",
      }),
    );
    return issues;
  }

  pushDuplicateValueIssues({
    issues,
    path: "spec.columns",
    values: args.spec.columns,
  });
  args.spec.columns.forEach((column, index) => {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: `spec.columns[${index}]`,
      fieldName: column,
      role: "any",
    });
  });

  const allowedColumns = new Set(args.spec.columns);
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.columnLabels",
    entries: args.spec.componentProps?.columnLabels,
    allowedKeys: allowedColumns,
    emptyAllowedMessage:
      "columnLabels requires selected columns in spec.columns",
  });
  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.columnSizing",
    entries: args.spec.componentProps?.columnSizing,
    allowedKeys: allowedColumns,
    emptyAllowedMessage:
      "columnSizing requires selected columns in spec.columns",
  });

  for (const [key, sizing] of Object.entries(
    args.spec.componentProps?.columnSizing ?? {},
  )) {
    if (
      sizing &&
      sizing.size === null &&
      sizing.minSize === null &&
      sizing.maxSize === null
    ) {
      issues.push(
        buildSpecValidationIssue({
          path: `spec.componentProps.columnSizing.${key}`,
          code: "empty-column-sizing",
          message: "must include at least one of size, minSize, or maxSize",
        }),
      );
    }
  }

  const defaultSortKey = args.spec.componentProps?.defaultSort?.key ?? null;
  if (defaultSortKey && !allowedColumns.has(defaultSortKey)) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.componentProps.defaultSort.key",
        code: "invalid-default-sort-key",
        message: "must reference a selected table column",
        expected: args.spec.columns.join(", "),
        actual: defaultSortKey,
      }),
    );
  }

  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleColumns",
    selectedKeys: args.spec.componentProps?.defaultVisibleColumns ?? [],
    allowedKeys: allowedColumns,
    emptyAllowedMessage:
      "defaultVisibleColumns requires selected columns in spec.columns",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.columnPinning.left",
    selectedKeys: args.spec.componentProps?.columnPinning?.left ?? [],
    allowedKeys: allowedColumns,
    emptyAllowedMessage:
      "columnPinning.left requires selected columns in spec.columns",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.columnPinning.right",
    selectedKeys: args.spec.componentProps?.columnPinning?.right ?? [],
    allowedKeys: allowedColumns,
    emptyAllowedMessage:
      "columnPinning.right requires selected columns in spec.columns",
  });

  const leftPinned = args.spec.componentProps?.columnPinning?.left ?? [];
  const rightPinned = args.spec.componentProps?.columnPinning?.right ?? [];
  pushDuplicateValueIssues({
    issues,
    path: "spec.componentProps.columnPinning.left",
    values: leftPinned,
  });
  pushDuplicateValueIssues({
    issues,
    path: "spec.componentProps.columnPinning.right",
    values: rightPinned,
  });
  const leftSet = new Set(leftPinned);
  for (const [index, key] of rightPinned.entries()) {
    if (leftSet.has(key)) {
      issues.push(
        buildSpecValidationIssue({
          path: `spec.componentProps.columnPinning.right[${index}]`,
          code: "duplicate-pinned-column",
          message: `column '${key}' cannot be pinned to both left and right`,
          actual: key,
        }),
      );
    }
  }

  const rowCount = args.dataset.rows.length;
  const topPinned = args.spec.componentProps?.rowPinning?.top ?? [];
  const bottomPinned = args.spec.componentProps?.rowPinning?.bottom ?? [];
  pushDuplicateValueIssues({
    issues,
    path: "spec.componentProps.rowPinning.top",
    values: topPinned.map(String),
  });
  pushDuplicateValueIssues({
    issues,
    path: "spec.componentProps.rowPinning.bottom",
    values: bottomPinned.map(String),
  });
  const topSet = new Set(topPinned);
  for (const [index, rowIndex] of topPinned.entries()) {
    if (rowIndex < 0 || rowIndex >= rowCount) {
      issues.push(
        buildSpecValidationIssue({
          path: `spec.componentProps.rowPinning.top[${index}]`,
          code: "invalid-row-index",
          message: `row index ${rowIndex} is outside the dataset row range`,
          expected: `0..${Math.max(0, rowCount - 1)}`,
          actual: String(rowIndex),
        }),
      );
    }
  }
  for (const [index, rowIndex] of bottomPinned.entries()) {
    if (rowIndex < 0 || rowIndex >= rowCount) {
      issues.push(
        buildSpecValidationIssue({
          path: `spec.componentProps.rowPinning.bottom[${index}]`,
          code: "invalid-row-index",
          message: `row index ${rowIndex} is outside the dataset row range`,
          expected: `0..${Math.max(0, rowCount - 1)}`,
          actual: String(rowIndex),
        }),
      );
    }
    if (topSet.has(rowIndex)) {
      issues.push(
        buildSpecValidationIssue({
          path: `spec.componentProps.rowPinning.bottom[${index}]`,
          code: "duplicate-row-pin",
          message: `row index ${rowIndex} cannot be pinned to both top and bottom`,
          actual: String(rowIndex),
        }),
      );
    }
  }

  return issues;
}

/**
 * structured authoring payload を `table` 用の renderer-ready spec へ変換する。
 */
export function compileTableSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<TableSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: TableAuthoring;
}): TableSpec {
  const columns =
    args.view.columns.length > 0
      ? args.view.columns
      : resolveDefaultTableColumns(args.dataset);

  return {
    type: "table",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    columns,
    limit: args.view.limit,
    componentProps: {
      columnLabels: buildFieldLabelMap(args.dataset, columns),
      defaultSort: args.view.sort,
      defaultVisibleColumns: resolveDefaultVisibleColumns(
        args.dataset,
        columns,
      ),
    },
  };
}

/**
 * table 用の UI element を構築する。
 */
export function buildTableElement(
  args: BuildVizElementArgs<TableSpec>,
): BuiltVizElement {
  return {
    elementKey: args.tableKey,
    element: {
      type: "VizTable",
      props: {
        dataPath: args.dataPath,
        columns: args.spec.columns,
        limit: args.spec.limit,
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
